/**
 * flip-renderer.ts — FlipBookRenderer
 *
 * Clean-room implementation of the BookRenderer interface.
 * Renders a BookContent manifest into the #book-viewport element provided
 * by the AppShell.
 *
 * Features implemented:
 *  - Double / single-page responsive layout (ResizeObserver, 640 px breakpoint)
 *  - CSS page-flip animation (fade + slide, no external dependency)
 *  - Spread navigation: nextPage, prevPage, goToPage (1-based spread index)
 *  - Page numbers displayed per page and in the bottom indicator bar
 *  - Table-of-contents slide-in panel (jump to spread, Italian labels)
 *  - German-language audio playback (inline and overlay positions)
 *  - Non-interactive tricolor Italian badge (corner or overlay position)
 *  - Absolute-positioned overlays (audio, badge)
 *  - Image-only full-bleed pages
 *  - Graceful empty state when manifest has no spreads yet
 */

import type { BookRenderer } from "./renderer";
import type { AudioAsset, BookContent, Overlay, BookPage } from "./content";
import "./flip-renderer.css";

/** Viewport width (px) below which single-page mode activates. */
const SINGLE_PAGE_BREAKPOINT = 640;

/** Duration of the page-flip fade+slide animation in milliseconds. */
const FLIP_MS = 220;

type TemplateVariant =
  | "opener"
  | "narrative"
  | "accented"
  | "bottom-vignette"
  | "exercise"
  | "illustrated-audio"
  | "panorama"
  | "full-bleed";

/**
 * Explicit post-index story template mapping (0-based spread index).
 * Keeps the first story block deterministic and avoids heuristic drift.
 */
const POST_INDEX_STORY_TEMPLATE_MAP: Record<number, Partial<Record<"left" | "right", TemplateVariant>>> = {
  3: { left: "full-bleed", right: "opener" },
  4: { left: "narrative", right: "bottom-vignette" },
  5: { left: "opener", right: "full-bleed" },
  6: { left: "narrative", right: "narrative" },
  7: { left: "panorama", right: "panorama" },
  8: { left: "narrative", right: "narrative" },
  9: { left: "bottom-vignette", right: "narrative" },
  10: { left: "accented", right: "accented" },
  11: { left: "narrative", right: "bottom-vignette" },
  12: { left: "opener", right: "full-bleed" },
  13: { left: "narrative", right: "narrative" },
  14: { left: "narrative", right: "narrative" },
};

export class FlipBookRenderer implements BookRenderer {
  private book: BookContent;
  private spreadIndex = 0;
  private viewport: HTMLElement | null = null;
  private spreadEl: HTMLElement | null = null;
  private indicatorEl: HTMLElement | null = null;
  private tocPanelEl: HTMLElement | null = null;
  private isAnimating = false;
  private singlePage = false;
  private ro: ResizeObserver | null = null;
  private readonly tocSpreadIndex: number;

  constructor(book: BookContent) {
    this.book = book;
    this.tocSpreadIndex = this.findTocSpreadIndex();
  }

  // ── BookRenderer interface ────────────────────────────────────────────────

  get pageCount(): number {
    return this.book.spreads.length;
  }

  get currentPage(): number {
    return this.spreadIndex + 1;
  }

  mount(viewport: HTMLElement): void {
    this.viewport = viewport;
    viewport.innerHTML = "";
    viewport.classList.add("fl-viewport");

    // Spread container — pages are rendered inside here
    const spreadEl = document.createElement("div");
    spreadEl.className = "fl-spread";
    this.spreadEl = spreadEl;

    // TOC slide-in panel
    const tocPanel = document.createElement("div");
    tocPanel.id = "toc-panel";
    tocPanel.className = "fl-toc";
    tocPanel.setAttribute("role", "navigation");
    tocPanel.setAttribute("aria-label", "Table of contents / Indice / Inhaltsverzeichnis");
    tocPanel.setAttribute("hidden", "");
    this.tocPanelEl = tocPanel;

    // Bottom chrome: indicator + optional TOC toggle
    const chrome = document.createElement("div");
    chrome.className = "fl-chrome";

    const indicator = document.createElement("span");
    indicator.className = "fl-indicator";
    indicator.setAttribute("aria-live", "polite");
    this.indicatorEl = indicator;
    chrome.appendChild(indicator);

    if (this.book.toc.length > 0) {
      const tocBtn = document.createElement("button");
      tocBtn.id = "btn-toc";
      tocBtn.type = "button";
      tocBtn.className = "fl-toc-btn";
      tocBtn.title = "Indice / Inhaltsverzeichnis";
      tocBtn.setAttribute("aria-label", "Apri indice");
      tocBtn.textContent = "☰";
      tocBtn.addEventListener("click", () => this.toggleToc());
      chrome.appendChild(tocBtn);
    }

    viewport.appendChild(spreadEl);
    viewport.appendChild(tocPanel);
    viewport.appendChild(chrome);

    // Initial responsive check
    this.singlePage = viewport.clientWidth < SINGLE_PAGE_BREAKPOINT;
    viewport.dataset.pageCount = String(this.book.spreads.length);

    this.ro = new ResizeObserver(() => {
      const narrow = (this.viewport?.clientWidth ?? 0) < SINGLE_PAGE_BREAKPOINT;
      if (narrow !== this.singlePage) {
        this.singlePage = narrow;
        this.renderSpread();
        this.updateViewportAttrs();
      }
    });
    this.ro.observe(viewport);

    // Escape key closes the TOC panel
    document.addEventListener("keydown", (e) => this._onKeyDown(e));

    this.buildTocPanel();
    this.renderSpread();

    // Expose renderer globally for test helpers and debug console
    (window as unknown as Record<string, unknown>).__renderer = this;
  }

  nextPage(): void {
    if (this.isAnimating) return;
    if (this.spreadIndex + 1 >= this.book.spreads.length) return;
    this.animateTo(this.spreadIndex + 1, "forward");
  }

  prevPage(): void {
    if (this.isAnimating) return;
    if (this.spreadIndex - 1 < 0) return;
    this.animateTo(this.spreadIndex - 1, "backward");
  }

  /** Jump to a spread by 1-based index (matches the BookRenderer contract). */
  goToPage(page: number): void {
    const idx = page - 1;
    if (idx < 0 || idx >= this.book.spreads.length) return;
    if (idx === this.spreadIndex) return;
    this.animateTo(idx, idx > this.spreadIndex ? "forward" : "backward");
  }

  // ── Animation ─────────────────────────────────────────────────────────────

  private animateTo(idx: number, dir: "forward" | "backward"): void {
    const el = this.spreadEl;
    if (!el) return;

    this.isAnimating = true;
    this.hideToc();
    this.pauseAllAudio();

    // Exit: fade out and slide toward the turn direction
    el.style.opacity = "0";
    el.style.transform = `translateX(${dir === "forward" ? "-3%" : "3%"})`;

    setTimeout(() => {
      this.spreadIndex = idx;
      this.renderSpread();

      // Snap to the enter position (opposite side) without transition
      el.style.transition = "none";
      el.style.opacity = "0";
      el.style.transform = `translateX(${dir === "forward" ? "3%" : "-3%"})`;

      // Force reflow so the snap takes effect before re-enabling transition
      void el.offsetHeight;

      // Animate back to rest
      el.style.transition = "";
      el.style.opacity = "1";
      el.style.transform = "";

      this.isAnimating = false;
    }, FLIP_MS);
  }

  // ── Spread rendering ──────────────────────────────────────────────────────

  private renderSpread(): void {
    const el = this.spreadEl;
    if (!el) return;

    el.innerHTML = "";
    el.className = "fl-spread";
    el.dataset.spreadIndex = String(this.spreadIndex);

    const spread = this.book.spreads[this.spreadIndex];

    if (!spread) {
      delete el.dataset.spreadPairing;
      delete el.dataset.postIndex;
      const msg = document.createElement("div");
      msg.className = "fl-empty";
      msg.textContent =
        this.book.spreads.length === 0
          ? "Contenuto in preparazione — Inhalt in Vorbereitung"
          : "Pagina non disponibile";
      el.appendChild(msg);
      this.updateIndicator();
      return;
    }

    el.dataset.spreadPairing = this.getSpreadPairing(spread);
    el.dataset.postIndex = this.isPostIndexSpread(this.spreadIndex) ? "true" : "false";

    if (this.singlePage) {
      el.classList.add("fl-spread--single");
      const page = this.pickSinglePage(spread.left, spread.right);
      if (page) el.appendChild(this.buildPage(page, "single", spread, this.spreadIndex));
    } else {
      el.appendChild(
        spread.left ? this.buildPage(spread.left, "left", spread, this.spreadIndex) : this.blankPage("left"),
      );
      el.appendChild(
        spread.right ? this.buildPage(spread.right, "right", spread, this.spreadIndex) : this.blankPage("right"),
      );
    }

    this.updateIndicator();
    this.updateViewportAttrs();
  }

  private blankPage(side: "left" | "right"): HTMLElement {
    const el = document.createElement("div");
    el.className = `fl-page fl-page--${side} fl-page--blank`;
    el.dataset.pageType = "blank";
    el.classList.add("fl-page--type-blank");
    return el;
  }

  private pickSinglePage(left: BookPage | null, right: BookPage | null): BookPage | null {
    const candidates = [
      { page: left, side: "left" as const },
      { page: right, side: "right" as const },
    ].filter((candidate): candidate is { page: BookPage; side: "left" | "right" } => candidate.page !== null);

    if (candidates.length === 0) return null;

    return candidates.reduce((best, candidate) =>
      this.getSinglePagePriority(candidate.page, candidate.side) >
      this.getSinglePagePriority(best.page, best.side)
        ? candidate
        : best,
    ).page;
  }

  private getSinglePagePriority(page: BookPage, side: "left" | "right"): number {
    const hasText = page.textBlocks.length > 0 ? 1000 : 0;
    const hasMedia = page.audio.length + page.images.length > 0 ? 100 : 0;
    const displayScore =
      page.displayLang === "both" ? 300 : page.displayLang === "it" ? 200 : 100;
    const richnessScore = page.textBlocks.length * 5 + page.audio.length * 2 + page.images.length;
    const sideTieBreaker = side === "right" ? 1 : 0;
    return hasText + hasMedia + displayScore + richnessScore + sideTieBreaker;
  }

  // ── Page building ─────────────────────────────────────────────────────────

  private buildPage(
    page: BookPage,
    pos: "left" | "right" | "single",
    spread: BookContent["spreads"][number],
    spreadIndex: number,
  ): HTMLElement {
    const el = document.createElement("article");
    el.className = `fl-page fl-page--${pos}`;
    el.dataset.displayLang = page.displayLang;
    el.dataset.pageType = page.pageType;
    el.dataset.spreadIndex = String(spreadIndex);
    el.classList.add(`fl-page--type-${page.pageType}`);

    const actualSide = this.resolveActualSide(page, pos, spread);
    el.dataset.pageSide = actualSide;

    if (page.images.length > 0) el.classList.add("fl-page--has-image");
    if (page.textBlocks.length > 0) el.classList.add("fl-page--has-text");
    if (page.audio.length > 0 || page.overlays.some((overlay) => overlay.type === "audio")) {
      el.classList.add("fl-page--has-audio");
    }
    if (page.pageType === "toc" && this.book.toc.length > 0) {
      el.classList.add("fl-page--has-toc");
    }
    if (page.audio.length > 1) el.classList.add("fl-page--multi-audio");
    if (this.isPostIndexSpread(spreadIndex)) {
      el.dataset.postIndex = "true";
      el.classList.add("fl-page--post-index");
    }

    const templateVariant = this.getTemplateVariant(page, spread, actualSide, spreadIndex);
    if (templateVariant) {
      el.dataset.templateVariant = templateVariant;
      el.classList.add(`fl-page--template-${templateVariant}`);
    }
    const imageShape = this.getPrimaryImageShape(page);
    if (imageShape) {
      el.dataset.imageShape = imageShape;
    }
    if (this.isShortCopyPage(page, spreadIndex)) {
      el.dataset.copyDensity = "short";
    }

    if (page.tocAnchor) {
      el.id = page.tocAnchor;
    }

    const inner = document.createElement("div");
    inner.className = "fl-page-inner";
    inner.classList.add(`fl-page-inner--type-${page.pageType}`);

    // ── Images ────────────────────────────────────────────────────────────
    if (page.images.length > 0) {
      const imgWrap = document.createElement("div");
      imgWrap.className = "fl-page-img-wrap";
      imgWrap.classList.add(`fl-page-img-wrap--type-${page.pageType}`);
      if (this.shouldUseFullBleedImage(page)) {
        imgWrap.classList.add("fl-page-img-wrap--full");
      }

      const img = document.createElement("img");
      img.src = page.images[0].url;
      img.alt = page.images[0].alt ?? "";
      img.className = "fl-page-img";
      img.loading = "lazy";
      if (page.images[0].width) img.width = page.images[0].width;
      if (page.images[0].height) img.height = page.images[0].height;

      imgWrap.appendChild(img);
      inner.appendChild(imgWrap);
    }

    const pageToc = this.buildPageToc(page);
    if (pageToc) {
      inner.appendChild(pageToc);
    }

    // ── Text blocks ───────────────────────────────────────────────────────
    // image-only pages carry no text column; all other types may have text.
    if (page.textBlocks.length > 0 && page.pageType !== "image-only") {
      const textEl = document.createElement("div");
      textEl.className = "fl-page-text";
      textEl.classList.add(`fl-page-text--type-${page.pageType}`);
      for (const group of this.getRenderableTextGroups(page)) {
        textEl.appendChild(this.buildLanguageGroup(group.lang, group.paragraphs));
      }
      inner.appendChild(textEl);
    }

    // ── Inline audio (no overlay position required) ───────────────────────
    if (page.audio.length > 0) {
      const audioGroup = document.createElement("div");
      audioGroup.className = "fl-page-audio";
      audioGroup.classList.add(page.audio.length > 1 ? "fl-page-audio--multi" : "fl-page-audio--single");
      for (const asset of page.audio) {
        audioGroup.appendChild(this.buildAudioPlayer(asset));
      }
      inner.appendChild(audioGroup);
    }

    el.appendChild(inner);

    // ── Absolutely-positioned overlays ────────────────────────────────────
    for (const overlay of page.overlays) {
      el.appendChild(this.buildOverlay(overlay));
    }

    // ── Translated-side badge (layout-engine corner placement) ────────────
    if (page.showTranslatedBadge) {
      const badge = this.buildBadge();
      badge.classList.add("fl-badge--corner");
      el.appendChild(badge);
    }

    // ── Page number ───────────────────────────────────────────────────────
    if (page.pageNumber !== null) {
      const num = document.createElement("span");
      num.className = "fl-page-num";
      num.setAttribute("aria-hidden", "true");
      num.textContent = String(page.pageNumber);
      el.appendChild(num);
    }

    return el;
  }

  private shouldUseFullBleedImage(page: BookPage): boolean {
    return page.pageType === "image-only" || page.pageType === "cover" || page.pageType === "imprint";
  }

  private findTocSpreadIndex(): number {
    return this.book.spreads.findIndex(
      (spread) => spread.left?.pageType === "toc" || spread.right?.pageType === "toc",
    );
  }

  private isPostIndexSpread(spreadIndex: number): boolean {
    return this.tocSpreadIndex >= 0 && spreadIndex > this.tocSpreadIndex;
  }

  private getSpreadPairing(spread: BookContent["spreads"][number]): string {
    const leftType = spread.left?.pageType ?? "blank";
    const rightType = spread.right?.pageType ?? "blank";
    return `${leftType}-${rightType}`;
  }

  private resolveActualSide(
    page: BookPage,
    pos: "left" | "right" | "single",
    spread: BookContent["spreads"][number],
  ): "left" | "right" {
    if (pos === "left" || pos === "right") return pos;
    if (spread.left === page) return "left";
    if (spread.right === page) return "right";
    return page.side;
  }

  private getTemplateVariant(
    page: BookPage,
    spread: BookContent["spreads"][number],
    actualSide: "left" | "right",
    spreadIndex: number,
  ): string | null {
    if (!this.isPostIndexSpread(spreadIndex)) return null;

    const mappedVariant = this.getExplicitStoryTemplateVariant(spreadIndex, actualSide, page.pageType);
    if (mappedVariant) {
      return mappedVariant;
    }

    const opposite = actualSide === "left" ? spread.right : spread.left;

    if (page.pageType === "text-spread") {
      if (spreadIndex === 4 && actualSide === "right" && page.images.length > 0) {
        return "bottom-vignette";
      }
      if (this.isExerciseLikePage(page)) {
        return "exercise";
      }
      if (opposite?.pageType === "image-only") {
        return "opener";
      }
      if (page.images.length > 0) {
        return "accented";
      }
      return "narrative";
    }

    if (page.pageType === "image-only") {
      if (opposite?.pageType === "image-only" && page.audio.length > 0) {
        return "illustrated-audio";
      }
      if (opposite?.pageType === "image-only") {
        return "panorama";
      }
      return "full-bleed";
    }

    return null;
  }

  private getExplicitStoryTemplateVariant(
    spreadIndex: number,
    side: "left" | "right",
    pageType: BookPage["pageType"],
  ): TemplateVariant | null {
    const spreadMap = POST_INDEX_STORY_TEMPLATE_MAP[spreadIndex];
    const mapped = spreadMap?.[side];
    if (!mapped) return null;

    if (pageType === "text-spread") {
      return mapped === "full-bleed" || mapped === "panorama" || mapped === "illustrated-audio" ? null : mapped;
    }
    if (pageType === "image-only") {
      return mapped === "full-bleed" || mapped === "panorama" || mapped === "illustrated-audio" ? mapped : null;
    }

    return null;
  }

  private isExerciseLikePage(page: BookPage): boolean {
    if (page.audio.length > 0 || page.textBlocks.length > 2) return false;

    const rawText = page.textBlocks
      .flatMap((block) => [block.de, block.it])
      .filter((text): text is string => Boolean(text))
      .join(" ");

    return /übungen|esercizi|gehe zu|vai a/i.test(rawText);
  }

  private isShortCopyPage(page: BookPage, spreadIndex: number): boolean {
    if (!this.isPostIndexSpread(spreadIndex) || page.pageType !== "text-spread") return false;

    const paragraphs = this.getVisibleParagraphs(page);
    if (paragraphs.length === 0 || paragraphs.length > 3) return false;

    const totalChars = paragraphs
      .map((paragraph) => paragraph.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim())
      .join(" ")
      .trim().length;

    return totalChars > 0 && totalChars <= 340;
  }

  private getPrimaryImageShape(page: BookPage): "landscape" | "portrait" | "square" | null {
    const primaryImage = page.images[0];
    if (!primaryImage?.width || !primaryImage.height) return null;

    if (primaryImage.width >= primaryImage.height * 1.15) {
      return "landscape";
    }
    if (primaryImage.height >= primaryImage.width * 1.15) {
      return "portrait";
    }
    return "square";
  }

  private getVisibleParagraphs(page: BookPage): string[] {
    const deParagraphs = page.textBlocks.map((block, idx) => {
      if (!block.de?.trim()) {
        throw new Error(
          `[libro-leone] textBlocks[].de is required for render output (page ${page.pageNumber ?? "?"}, block ${idx}).`,
        );
      }
      return block.de;
    });

    if (page.displayLang === "de") {
      return deParagraphs;
    }

    const itParagraphs = page.textBlocks.map((block, idx) =>
      this.requireItalianText(block.it, page, idx),
    );

    return page.displayLang === "it" ? itParagraphs : [...itParagraphs, ...deParagraphs];
  }

  private buildPageToc(page: BookPage): HTMLElement | null {
    if (page.pageType !== "toc" || this.book.toc.length === 0) return null;

    const section = document.createElement("section");
    section.className = "fl-page-toc";

    const heading = document.createElement("h2");
    heading.className = "fl-page-toc-heading";
    heading.textContent = "Indice";
    section.appendChild(heading);

    const list = document.createElement("ol");
    list.className = "fl-page-toc-list";

    for (const entry of this.book.toc) {
      const li = document.createElement("li");
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "fl-page-toc-entry";
      btn.textContent = entry.labelIt ?? entry.labelDe;
      btn.addEventListener("click", () => this.goToPage(entry.spreadIndex + 1));
      li.appendChild(btn);
      list.appendChild(li);
    }

    section.appendChild(list);
    return section;
  }

  private getRenderableTextGroups(
    page: BookPage,
  ): Array<{ lang: "de" | "it"; paragraphs: string[] }> {
    const deParagraphs = this.getVisibleParagraphs({ ...page, displayLang: "de" });

    if (page.displayLang === "de") {
      return [{ lang: "de", paragraphs: deParagraphs }];
    }

    const itParagraphs = this.getVisibleParagraphs({ ...page, displayLang: "it" });

    if (page.displayLang === "it") {
      return [{ lang: "it", paragraphs: itParagraphs }];
    }

    return [
      { lang: "it", paragraphs: itParagraphs },
      { lang: "de", paragraphs: deParagraphs },
    ];
  }

  private requireItalianText(blockIt: string | undefined, page: BookPage, blockIndex: number): string {
    if (!blockIt?.trim()) {
      throw new Error(
        `[libro-leone] Missing Italian translation for ${page.displayLang} page ${page.pageNumber ?? "?"}, block ${blockIndex}.`,
      );
    }
    return blockIt;
  }

  private buildLanguageGroup(lang: "de" | "it", paragraphs: string[]): HTMLElement {
    const group = document.createElement("div");
    group.className = `fl-page-text-group fl-page-text-group--${lang}`;
    group.lang = lang;

    for (const paragraph of paragraphs) {
      const p = document.createElement("p");
      p.className = `fl-text fl-text--${lang}`;
      p.innerHTML = paragraph;
      group.appendChild(p);
    }

    return group;
  }

  // ── Audio player ──────────────────────────────────────────────────────────

  private buildAudioPlayer(asset: AudioAsset): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "fl-audio";

    const audioEl = document.createElement("audio");
    audioEl.src = asset.url;
    audioEl.preload = "none";
    audioEl.lang = asset.lang;

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "fl-audio-btn";
    const ariaLabel = asset.label ?? "Audio abspielen";
    btn.setAttribute("aria-label", ariaLabel);
    btn.title = ariaLabel;
    btn.textContent = "🔊";

    const sync = (playing: boolean): void => {
      btn.textContent = playing ? "⏸" : "🔊";
      btn.classList.toggle("fl-audio-btn--playing", playing);
    };

    btn.addEventListener("click", () => {
      audioEl.paused ? audioEl.play().catch(console.warn) : audioEl.pause();
    });
    audioEl.addEventListener("play", () => sync(true));
    audioEl.addEventListener("pause", () => sync(false));
    audioEl.addEventListener("ended", () => sync(false));

    wrap.appendChild(btn);
    wrap.appendChild(audioEl);
    return wrap;
  }

  // ── Overlay builder ───────────────────────────────────────────────────────

  private buildOverlay(overlay: Overlay): HTMLElement {
    const el = document.createElement("div");
    el.className = "fl-overlay";
    el.style.left = overlay.unit === "percent" ? `${overlay.x}%` : `${overlay.x}px`;
    el.style.top = overlay.unit === "percent" ? `${overlay.y}%` : `${overlay.y}px`;

    if (overlay.type === "audio") {
      el.appendChild(this.buildAudioPlayer(overlay.audioRef));
    } else {
      // overlay.type === "badge"
      el.appendChild(this.buildBadge());
    }

    return el;
  }

  // ── Italian tricolor badge ────────────────────────────────────────────────

  private buildBadge(): HTMLElement {
    const badge = document.createElement("div");
    badge.className = "fl-badge";
    badge.setAttribute("role", "img");
    badge.setAttribute("aria-label", "Traduzione italiana");

    for (const color of ["#009246", "#ffffff", "#ce2b37"]) {
      const stripe = document.createElement("span");
      stripe.style.background = color;
      badge.appendChild(stripe);
    }

    return badge;
  }

  // ── TOC panel ─────────────────────────────────────────────────────────────

  private buildTocPanel(): void {
    const panel = this.tocPanelEl;
    if (!panel || this.book.toc.length === 0) return;

    const heading = document.createElement("h2");
    heading.className = "fl-toc-heading";
    heading.textContent = "Indice";
    panel.appendChild(heading);

    const list = document.createElement("ol");
    list.className = "fl-toc-list";

    for (const entry of this.book.toc) {
      const li = document.createElement("li");
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "fl-toc-entry";
      btn.textContent = entry.labelIt ?? entry.labelDe;
      btn.addEventListener("click", () => {
        // spreadIndex is 0-based; goToPage expects 1-based
        this.goToPage(entry.spreadIndex + 1);
        this.hideToc();
      });
      li.appendChild(btn);
      list.appendChild(li);
    }

    panel.appendChild(list);
  }

  private toggleToc(): void {
    const panel = this.tocPanelEl;
    if (!panel) return;
    panel.hasAttribute("hidden")
      ? panel.removeAttribute("hidden")
      : panel.setAttribute("hidden", "");
  }

  private hideToc(): void {
    this.tocPanelEl?.setAttribute("hidden", "");
  }

  // ── Page indicator ────────────────────────────────────────────────────────

  private updateIndicator(): void {
    const el = this.indicatorEl;
    if (!el) return;

    if (this.book.spreads.length === 0) {
      el.textContent = "";
      return;
    }

    const spread = this.book.spreads[this.spreadIndex];
    if (!spread) {
      el.textContent = `${this.spreadIndex + 1} / ${this.book.spreads.length}`;
      return;
    }

    const ln = spread.left?.pageNumber ?? null;
    const rn = spread.right?.pageNumber ?? null;

    if (ln !== null && rn !== null) {
      el.textContent = `${ln} – ${rn}`;
    } else if (ln !== null) {
      el.textContent = String(ln);
    } else if (rn !== null) {
      el.textContent = String(rn);
    } else {
      // Unnumbered spread (cover, blank): show spread position
      el.textContent = `${this.spreadIndex + 1} / ${this.book.spreads.length}`;
    }
  }

  // ── Viewport data attributes ───────────────────────────────────────────────

  private updateViewportAttrs(): void {
    if (!this.viewport) return;
    this.viewport.dataset.currentPage = String(this.spreadIndex + 1);
    this.viewport.dataset.mode = this.singlePage ? "single" : "double";
  }

  // ── Audio lifecycle ───────────────────────────────────────────────────────

  private pauseAllAudio(): void {
    this.spreadEl
      ?.querySelectorAll("audio")
      .forEach((a) => (a as HTMLAudioElement).pause());
  }

  // ── Keyboard handler ──────────────────────────────────────────────────────

  private _onKeyDown(e: KeyboardEvent): void {
    if (e.key === "Escape") this.hideToc();
  }

}
